import React from 'react';
import { Button, Card, Col, Row, Table, Tag, Typography } from 'antd';
import {
  AttendanceActionDto,
  AttendanceClient,
  AttendanceEventDto,
  CurrentEmployeeModel,
} from '@@api';
import './WebClockView.scss';
import dayjs from 'dayjs';
import { ColumnType } from 'antd/lib/table';
import { RECORD_ID } from '@@common';
import {
  useOfflineCommand,
  useOfflineGetOrLoad,
} from '@4r/module-common-mf-app';
import { useApiClient } from '@@hooks';
import { DATE_FORMAT, TIME_FORMAT } from '../../common/formats';
import EventIcon from './EventIcon';
import clockAction from '../../services/clockAction';

const isActionDisabled = (
  action: AttendanceActionDto,
  employee: CurrentEmployeeModel,
): boolean => {
  if (
    employee.lastEvent &&
    action.previousActionTypeIds.indexOf(employee.lastEvent.typeId ?? '') === -1
  ) {
    return true;
  }
  if (
    !employee.lastEvent &&
    employee.possibleActions.findIndex((x) => x.typeId === action.typeId) !== 0
  ) {
    return true;
  }
  return false;
};

const tableColumns: ColumnType<AttendanceEventDto>[] = [
  {
    title: '',
    dataIndex: 'typeId',
    key: 'typeId',
    width: '1em',
    render: (value) => <EventIcon iconType={value} />,
  },
  {
    title: 'Status',
    dataIndex: 'statusName',
    key: 'statusName',
  },
  {
    title: 'Date',
    dataIndex: 'timestamp',
    key: 'timestamp',
    width: '10em',
    render: (value) => dayjs(value).format(DATE_FORMAT),
  },
  {
    title: 'Time',
    dataIndex: 'timestamp',
    key: 'timestamp',
    width: '10em',
    render: (value) => dayjs(value).format(TIME_FORMAT),
  },
];

const WebClockView = () => {
  const attendanceClient = useApiClient(AttendanceClient);

  const [employee, , refreshEmployee] = useOfflineGetOrLoad(
    RECORD_ID.USER_ATTENDANCE_STATUS,
    attendanceClient,
    (s) => s.currentEmployee(),
  );
  const [commandPending, submitCommand] = useOfflineCommand();

  const onAction = (action: AttendanceActionDto) => {
    if (employee) {
      clockAction(
        submitCommand,
        employee.possibleActions,
        action.typeId,
        refreshEmployee,
      );
    }
  };

  return (
    <div className="web-clock-page">
      <Card bordered className="web-clock-page__card">
        <Row gutter={16} className="web-clock-page__status">
          <Col className="status__cell" xs={12} sm={12} md={6}>
            <Tag>Status</Tag>
            <Typography.Title level={2}>
              {employee?.lastEvent?.statusName}
            </Typography.Title>
          </Col>
          <Col className="status__cell" xs={12} sm={12} md={6}>
            <Tag>Time</Tag>
            <Typography.Title level={2}>
              {employee?.lastEvent?.timestamp
                ? dayjs(employee?.lastEvent?.timestamp).format(TIME_FORMAT)
                : null}
            </Typography.Title>
          </Col>
        </Row>
        <Row gutter={16}>
          {employee?.possibleActions &&
            employee.possibleActions.map((action) => (
              <Col key={action.typeId} flex="1">
                <Button
                  type="primary"
                  disabled={
                    isActionDisabled(action, employee) || commandPending
                  }
                  block
                  size="large"
                  className="web-clock-page__button"
                  icon={<EventIcon iconType={action.typeId} />}
                  onClick={() => onAction(action)}
                >
                  {action.typeName}
                </Button>
              </Col>
            ))}
        </Row>
      </Card>

      {employee?.events && (
        <Card
          bordered
          className="web-clock-page__card web-clock-page__cardtable"
        >
          <Typography.Title level={5}>Daily Time Events</Typography.Title>
          <Table
            dataSource={employee.events}
            rowKey="id"
            columns={tableColumns}
            pagination={{ pageSize: 100, hideOnSinglePage: true }}
          />
        </Card>
      )}
    </div>
  );
};

export default WebClockView;
